import { useState, useMemo } from 'react';
import { AlertTriangle, XCircle, FileWarning, Wrench, Shield, X, Check, Upload, FileText, ChevronDown, ArrowRightCircle, Lightbulb, Plus, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { DenialRiskResult, ActionableIssue } from '@/hooks/useDenialRiskAnalysis';

interface DenialRiskAlertPanelProps {
  result: DenialRiskResult;
  onDismiss: () => void;
  onApplyCodeFix?: (issue: ActionableIssue) => void;
  onApplySuggestedFix?: (issue: ActionableIssue) => void;
  onFileUpload?: (issue: ActionableIssue, file: File) => void;
  onAppendAddendum?: (issue: ActionableIssue, text: string) => void;
  onBypassToRcm?: (issues: ActionableIssue[]) => void;
  /** Delete a CPT code entirely (used for same-compartment conditional fixes) */
  onDeleteCode?: (code: string) => void;
  /** Surgeon acknowledges and proceeds — unresolved items flagged for office manager */
  onProceedWithPendingItems?: (issues: ActionableIssue[]) => void;
  /** Per-issue flag — record this single item for RCM follow-up without resolving it. */
  onFlagAndMoveOn?: (issue: ActionableIssue) => void;
  mode?: 'pre-auth' | 'post-op';
}

/**
 * Detect conditional proposed_fix_data like:
 * "Replace X with G0289 if separate compartment. If same compartment, delete the code entirely."
 * Returns [option1Label, option2Label] or null if not conditional.
 */
function splitConditionalFix(text: string): [string, string] | null {
  const splitIdx = text.search(/\.\s+If\s+/);
  if (splitIdx === -1) return null;
  const part1 = text.substring(0, splitIdx).trim();
  const part2 = text.substring(splitIdx + 1).trim();
  if (part1.length < 10 || part2.length < 10) return null;
  return [part1, part2];
}

const CATEGORY_CONFIG = {
  Coding: { icon: Wrench, color: 'text-primary', bgColor: 'bg-primary/5', borderColor: 'border-primary/20', label: 'Coding Issue' },
  Documentation: { icon: FileWarning, color: 'text-amber-600', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20', label: 'Documentation Gap' },
  Clinical_Threshold: { icon: AlertTriangle, color: 'text-destructive', bgColor: 'bg-destructive/10', borderColor: 'border-destructive/20', label: 'Clinical Threshold' },
};

function IssueCard({
  issue,
  index,
  onApplyCodeFix,
  onApplySuggestedFix,
  onFileUpload,
  onAppendAddendum,
  onDeleteCode,
  onFlagAndMoveOn,
}: {
  issue: ActionableIssue;
  index: number;
  onApplyCodeFix?: (issue: ActionableIssue) => void;
  onApplySuggestedFix?: (issue: ActionableIssue) => void;
  onFileUpload?: (issue: ActionableIssue, file: File) => void;
  onAppendAddendum?: (issue: ActionableIssue, text: string) => void;
  onDeleteCode?: (code: string) => void;
  onFlagAndMoveOn?: (issue: ActionableIssue) => void;
}) {
  const [applied, setApplied] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [addendumText, setAddendumText] = useState(issue.proposed_fix_data ?? '');
  const [addendumAppended, setAddendumAppended] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);

  const config = CATEGORY_CONFIG[issue.category] || CATEGORY_CONFIG.Documentation;
  const Icon = config.icon;
  const resolved = applied || flagged || addendumAppended || !!uploadedFile;

  const handleApplyFix = () => {
    onApplyCodeFix?.(issue);
    setApplied(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file.name);
      onFileUpload?.(issue, file);
    }
  };

  const handleAppendAddendum = () => {
    onAppendAddendum?.(issue, addendumText);
    setAddendumAppended(true);
  };

  return (
    <div className={`rounded-lg border ${config.borderColor} ${config.bgColor} p-3 space-y-2.5 ${resolved ? 'opacity-70' : ''}`}>
      {/* Issue header */}
      <div className="flex items-start gap-2.5">
        <div className={`w-7 h-7 rounded-full ${config.bgColor} flex items-center justify-center shrink-0 mt-0.5`}>
          <Icon className={`w-3.5 h-3.5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={issue.severity === 'critical' ? 'destructive' : 'outline'}
              className="text-[10px] uppercase tracking-wider"
            >
              {issue.severity}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {config.label}
            </Badge>
            {issue.code && (
              <Badge variant="outline" className="text-[10px] font-mono">
                {issue.code}
              </Badge>
            )}
          </div>
          <p className="text-sm text-foreground leading-relaxed mt-1.5 break-words">{issue.description}</p>

          {/* Detailed fix explanation — how to satisfy */}
          {issue.detailed_fix_explanation && !resolved && (
            <div className="mt-2 rounded-md bg-background/60 border border-border/40 p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Lightbulb className="w-3 h-3 text-amber-500 shrink-0" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  How to satisfy
                </span>
              </div>
              <p className="text-xs text-foreground/85 leading-relaxed break-words">
                {issue.detailed_fix_explanation}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Resolution action */}
      <div className="pl-9 space-y-2">
        {issue.resolution_type === 'auto_fix_code' && (() => {
          const conditional = issue.proposed_fix_data ? splitConditionalFix(issue.proposed_fix_data) : null;
          if (applied) {
            return (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <Check className="w-3.5 h-3.5" />
                Correction applied
              </div>
            );
          }
          if (conditional) {
            // Two-option conditional fix — show two distinct buttons
            const [option1, option2] = conditional;
            const isDeleteOption2 = /delete|remove/i.test(option2);
            return (
              <div className="space-y-1.5">
                <p className="text-[11px] text-muted-foreground font-medium">Select the correct scenario for this case:</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-auto min-h-[28px] w-full text-xs border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 whitespace-normal text-left py-1.5"
                  onClick={() => { onApplyCodeFix?.(issue); setApplied(true); }}
                >
                  <Check className="w-3 h-3 mr-1.5 shrink-0" />
                  {option1}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-auto min-h-[28px] w-full text-xs border-destructive/40 text-destructive hover:bg-destructive/10 whitespace-normal text-left py-1.5"
                  onClick={() => {
                    if (isDeleteOption2 && issue.code) onDeleteCode?.(issue.code);
                    setApplied(true);
                  }}
                >
                  <X className="w-3 h-3 mr-1.5 shrink-0" />
                  {option2}
                </Button>
              </div>
            );
          }
          return (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-800"
              onClick={handleApplyFix}
            >
              <Check className="w-3 h-3 mr-1.5" />
              Apply Correction: {issue.proposed_fix_data}
            </Button>
          );
        })()}

        {issue.resolution_type === 'require_upload' && (
          <div>
            {uploadedFile ? (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <Check className="w-3.5 h-3.5" />
                Uploaded: {uploadedFile}
              </div>
            ) : (
              <label className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/30 px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors">
                <Upload className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Drop or click to upload supporting document</span>
                <input type="file" className="hidden" onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
              </label>
            )}
          </div>
        )}

        {issue.resolution_type === 'propose_addendum' && (
          <div className="space-y-2">
            {addendumAppended ? (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <Check className="w-3.5 h-3.5" />
                Addendum appended to operative note
              </div>
            ) : (
              <>
                <Textarea
                  value={addendumText}
                  onChange={(e) => setAddendumText(e.target.value)}
                  className="text-xs min-h-[60px] bg-background/60 resize-none"
                  placeholder="Proposed addendum language..."
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-primary/40 text-primary hover:bg-primary/10"
                  onClick={handleAppendAddendum}
                  disabled={!addendumText.trim()}
                >
                  <FileText className="w-3 h-3 mr-1.5" />
                  Append to Operative Note
                </Button>
              </>
            )}
          </div>
        )}

        {(issue.resolution_type === 'suggest_fix' || issue.resolution_type === 'manual_dropdown') && issue.proposed_fix_data && (
          <div className="space-y-1.5">
            {applied ? (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <Check className="w-3.5 h-3.5" />
                Applied: {issue.proposed_fix_data}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
                  <span className="font-medium text-foreground">Suggested Fix:</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-auto min-h-[28px] text-xs border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-800 whitespace-normal text-left py-1.5"
                  onClick={() => {
                    onApplySuggestedFix?.(issue);
                    setApplied(true);
                  }}
                >
                  <Plus className="w-3 h-3 mr-1.5 shrink-0" />
                  {issue.proposed_fix_data}
                </Button>
              </div>
            )}
          </div>
        )}

        {(issue.resolution_type === 'suggest_fix' || issue.resolution_type === 'manual_dropdown') && !issue.proposed_fix_data && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ChevronDown className="w-3.5 h-3.5" />
            Requires manual clinical review — correct this in the case data above
          </div>
        )}

        {/* Deletion alternative — when removing the code is also a valid option */}
        {!resolved && issue.deletion_alternative && issue.code && onDeleteCode && (
          <div className="flex items-start gap-2 rounded-md border border-dashed border-destructive/30 bg-destructive/5 p-2">
            <X className="w-3.5 h-3.5 text-destructive mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-muted-foreground mb-1.5 break-words">
                Or remove this entirely if it's not clinically required:
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-auto min-h-[28px] text-xs border-destructive/40 text-destructive hover:bg-destructive/10 whitespace-normal text-left py-1.5"
                onClick={() => { onDeleteCode(issue.code!); setApplied(true); }}
              >
                <X className="w-3 h-3 mr-1.5 shrink-0" />
                {issue.deletion_alternative}
              </Button>
            </div>
          </div>
        )}

        {/* Per-issue Flag & Move On — ALWAYS available, isolated to this single issue.
            Does NOT submit the page. Surgeon explicitly proceeds via the bottom button. */}
        {!resolved && onFlagAndMoveOn && (
          <div className="flex items-start gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20 p-2">
            <ArrowRightCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-muted-foreground mb-1.5 break-words leading-relaxed">
                {issue.bypass_reasoning
                  ?? (issue.is_surgeon_bypassable
                      ? 'Office manager can close this gap before claim submission.'
                      : 'Flag for follow-up. Surgeon judgment required — flagging does NOT submit; proceed when ready via the button below.')}
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onFlagAndMoveOn(issue);
                  setFlagged(true);
                }}
              >
                <ArrowRightCircle className="w-3 h-3 mr-1.5" />
                Flag this issue
              </Button>
            </div>
          </div>
        )}

        {/* Resolved indicator (when flagged via per-issue Flag & Move On) */}
        {flagged && (
          <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 font-medium">
            <ArrowRightCircle className="w-3.5 h-3.5" />
            Flagged for office manager — proceed via the button below when ready
          </div>
        )}
      </div>
    </div>
  );
}

export function DenialRiskAlertPanel({
  result,
  onDismiss,
  onApplyCodeFix,
  onApplySuggestedFix,
  onFileUpload,
  onAppendAddendum,
  onBypassToRcm,
  onDeleteCode,
  onProceedWithPendingItems,
  onFlagAndMoveOn,
  mode = 'pre-auth',
}: DenialRiskAlertPanelProps) {
  const criticalCount = result.actionable_issues.filter(i => i.severity === 'critical').length;
  const warningCount = result.actionable_issues.filter(i => i.severity === 'warning').length;
  const isBlocked = result.verdict === 'BLOCKED';

  // Strategic bypass: available only if ALL remaining issues are surgeon-bypassable (admin-only)
  const allBypassable = useMemo(() => {
    if (result.actionable_issues.length === 0) return false;
    return result.actionable_issues.every(i => i.is_surgeon_bypassable === true);
  }, [result.actionable_issues]);

  const bypassableIssues = useMemo(
    () => result.actionable_issues.filter(i => i.is_surgeon_bypassable === true),
    [result.actionable_issues]
  );

  const nonBypassableCount = result.actionable_issues.filter(i => !i.is_surgeon_bypassable).length;

  return (
    <div className={`rounded-lg border-2 ${isBlocked ? 'border-destructive bg-destructive/5' : 'border-amber-500 bg-amber-500/5'} p-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${isBlocked ? 'bg-destructive/10' : 'bg-amber-500/10'} flex items-center justify-center shrink-0`}>
            <XCircle className={`w-5 h-5 ${isBlocked ? 'text-destructive' : 'text-amber-600'}`} />
          </div>
          <div>
            <h3 className={`text-base font-bold ${isBlocked ? 'text-destructive' : 'text-amber-700'} flex items-center gap-2`}>
              {mode === 'pre-auth' ? 'Pre-Auth' : 'Post-Op'} RCM Audit — {result.verdict}
              <Badge variant={isBlocked ? 'destructive' : 'outline'} className="text-[10px] uppercase tracking-wider">
                {criticalCount > 0 ? `${criticalCount} Critical` : `${warningCount} Warnings`}
              </Badge>
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isBlocked
                ? 'Resolve now or flag the issue for the office manager so the surgeon can keep moving.'
                : 'Warnings detected. Review recommended but not blocking.'}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onDismiss}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Actionable Issues */}
      {result.actionable_issues.length > 0 && (
        <div className="space-y-2">
          {result.actionable_issues.map((issue, i) => (
            <IssueCard
              key={i}
              issue={issue}
              index={i}
              onApplyCodeFix={onApplyCodeFix}
              onApplySuggestedFix={onApplySuggestedFix}
              onFileUpload={onFileUpload}
              onAppendAddendum={onAppendAddendum}
              onDeleteCode={onDeleteCode}
              onFlagAndMoveOn={onFlagAndMoveOn}
            />
          ))}
        </div>
      )}

      {/* Pre-auth criticals can be routed to staff, but they cannot unlock packet generation. */}
      {isBlocked && onProceedWithPendingItems && (
        <div className="rounded-lg border-2 border-amber-500/50 bg-amber-50/60 dark:bg-amber-950/20 p-3.5 space-y-2.5">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {mode === 'pre-auth' ? 'Route remaining items to staff hold' : 'Flag remaining items & move on'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                {mode === 'pre-auth'
                  ? 'Critical pre-auth issues can be assigned to the office manager. The surgeon leaves this case on staff hold and keeps moving; staff must correct it before payer submission.'
                  : 'Surgeon proceeds to reconciliation. Unresolved administrative items are routed to the office manager for follow-up before claim submission.'}
              </p>
            </div>
          </div>
          <Button
            variant={mode === 'pre-auth' ? 'outline' : 'default'}
            size="sm"
            className={mode === 'pre-auth'
              ? 'w-full border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 font-semibold h-10'
              : 'w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold h-10'}
            onClick={() => onProceedWithPendingItems(result.actionable_issues.filter(i => i.severity === 'critical'))}
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            {mode === 'pre-auth' ? 'Flag & Continue' : 'Flag & Move On'} — {result.actionable_issues.filter(i => i.severity === 'critical').length} item{result.actionable_issues.filter(i => i.severity === 'critical').length !== 1 ? 's' : ''} to office manager
          </Button>
        </div>
      )}

      {/* Strategic Bypass — only if ALL issues are admin/bypassable */}
      {mode === 'post-op' && isBlocked && allBypassable && onBypassToRcm && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
          <div className="flex items-start gap-2">
            <ArrowRightCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">
                All {bypassableIssues.length} remaining issue{bypassableIssues.length !== 1 ? 's are' : ' is'} administrative
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                No clinical dictation corrections are required. These items can be resolved by your RCM team without surgeon involvement.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-primary/40 text-primary hover:bg-primary/10"
            onClick={() => onBypassToRcm(bypassableIssues)}
          >
            <ArrowRightCircle className="w-3.5 h-3.5 mr-2" />
            Acknowledge Missing Admin Docs & Route to RCM Team
          </Button>
        </div>
      )}

      {/* Info: why bypass is locked */}
      {mode === 'post-op' && isBlocked && !allBypassable && bypassableIssues.length > 0 && (
        <div className="rounded-md bg-muted/30 p-2.5 text-xs text-muted-foreground border border-dashed">
          <span className="font-medium text-foreground">{nonBypassableCount} clinical issue{nonBypassableCount !== 1 ? 's' : ''}</span> must be resolved by the surgeon before the RCM bypass becomes available.
          {bypassableIssues.length > 0 && (
            <span> ({bypassableIssues.length} admin item{bypassableIssues.length !== 1 ? 's' : ''} can be routed to RCM after.)</span>
          )}
        </div>
      )}

      {/* Summary */}
      {result.summary && (
        <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground leading-relaxed border">
          <div className="flex items-center gap-1.5 mb-1 font-semibold text-foreground">
            <Shield className="w-3.5 h-3.5" />
            RCM Audit Summary
          </div>
          {result.summary}
        </div>
      )}
    </div>
  );
}
